import { Stack, Tooltip, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  CraneIcon,
  EmptyContainerHandlerIcon,
  EquipmentTypeIcon,
  ForkLiftIcon,
  RailMountedGantryIcon,
  ReachStackerIcon,
  RubberTyredGantryIcon,
  StraddleCarrierIcon,
  TerminalTruckIcon,
} from '@tom-ui/ui'
import { EquipmentTypes } from '@yard/app/api'
import { useYardStores } from '@yard/index'
import { yardBlockService } from '@yard/services/yardBlock.service'
import { FC, ReactNode, useState } from 'react'
import { EquipmentTypesMenu } from '../../atoms/EquipmentTypesMenu'
import { YardNodeHeaderTitle } from '../../atoms/YardNodeHeaderTitle'

type Props = {
  yardBlockId: string
  equipmentTypes: EquipmentTypes[]
  isEditable?: boolean
}

const equipmentTypeIcons: Record<EquipmentTypes, ReactNode> = {
  [EquipmentTypes.None]: null,
  [EquipmentTypes.Sts]: <CraneIcon fontSize='small' />,
  [EquipmentTypes.Rtg]: <RubberTyredGantryIcon fontSize='small' />,
  [EquipmentTypes.Rs]: <ReachStackerIcon fontSize='small' />,
  [EquipmentTypes.Ech]: <EmptyContainerHandlerIcon fontSize='small' />,
  [EquipmentTypes.Tt]: <TerminalTruckIcon fontSize='small' />,
  [EquipmentTypes.Sc]: <StraddleCarrierIcon fontSize='small' />,
  [EquipmentTypes.Fl]: <ForkLiftIcon fontSize='small' />,
  [EquipmentTypes.Rmg]: <RailMountedGantryIcon fontSize='small' />,
}

export const YardNodeHeaderEquipmentType: FC<Props> = props => {
  const { yardBlockId, equipmentTypes, isEditable } = props
  const { t } = useTranslate()
  const { appStore } = useYardStores()

  const [equipmentMenu, setEquipmentMenu] = useState<{
    mouseX: number
    mouseY: number
  } | null>(null)

  const visibleEquipmentTypes = equipmentTypes.slice(0, 2)
  const overflowCount = Math.max(equipmentTypes.length - visibleEquipmentTypes.length, 0)

  return (
    <>
      <YardNodeHeaderTitle
        onClick={e => {
          if (!isEditable) return
          setEquipmentMenu({
            mouseX: e.clientX,
            mouseY: e.clientY,
          })
        }}
        displayHoverEffect={isEditable}
        title={
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Typography variant='subtitle1' whiteSpace='nowrap'>
              {t('equip', 'Equip.:')}
            </Typography>
            {equipmentTypes.length === 0 ? (
              <Typography variant='subtitle1' whiteSpace='nowrap'>
                {t('none', 'None')}
              </Typography>
            ) : (
              <>
                {visibleEquipmentTypes.map(et => (
                  <Tooltip key={et} title={t(et)}>
                    <Stack component='span' direction='row' alignItems='center'>
                      {equipmentTypeIcons[et]}
                    </Stack>
                  </Tooltip>
                ))}
                {overflowCount > 0 && (
                  <Typography variant='subtitle1' whiteSpace='nowrap'>
                    +{overflowCount}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        }
        icon={<EquipmentTypeIcon />}
      />

      <EquipmentTypesMenu
        anchorPosition={
          equipmentMenu ? { top: equipmentMenu.mouseY, left: equipmentMenu.mouseX } : undefined
        }
        selectedEquipmentTypes={equipmentTypes}
        open={!!equipmentMenu}
        onClose={() => setEquipmentMenu(null)}
        onSelect={async (equipmentType: EquipmentTypes) => {
          try {
            const updatedEquipmentTypes = equipmentTypes.includes(equipmentType)
              ? equipmentTypes.filter(et => et !== equipmentType)
              : [...equipmentTypes, equipmentType]

            await yardBlockService.updateEquipmentTypes(yardBlockId, updatedEquipmentTypes)
          } catch (e: any) {
            appStore.setShowAlert(
              'error',
              t('updateEquipmentTypesError', 'Failed to update equipment types'),
            )
          }
        }}
      />
    </>
  )
}
